import React, { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  BarChart3,
  CheckCircle2,
  FilePlus,
  Link2,
  ListChecks,
  PlayCircle,
  Plug,
  RefreshCcw,
  Wrench
} from 'lucide-react';
import { adminReportingAPI } from '../api';

const tabs = [
  { id: 'dashboard', label: 'Reporting Dashboard', icon: BarChart3 },
  { id: 'builder', label: 'Report Builder', icon: Wrench },
  { id: 'apis', label: 'Advertiser APIs', icon: Plug }
];

const initialIntegrationForm = {
  name: '',
  endpoint_url: '',
  request_method: 'GET',
  poll_interval_minutes: '60',
  lookback_minutes: '30',
  response_path: 'data',
  record_id_field: 'id',
  increment_field: '',
  pixel_enabled: true,
  pixel_method: 'GET',
  pixel_url_template: ''
};

const initialConnectionForm = {
  name: '',
  connection_type: 'http',
  base_url: '',
  auth_type: 'none',
  auth_config: '{}',
  default_headers: '{}'
};

const initialReportForm = {
  name: '',
  dataset: 'signups',
  config: '{"group_by":"day"}'
};

const initialScheduleForm = {
  report_id: '',
  name: '',
  destination_url: '',
  payload_format: 'json',
  interval_minutes: '60'
};

const parseJsonField = (value, fallback = {}) => {
  try {
    const parsed = JSON.parse(value || '{}');
    if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
      return parsed;
    }
    return fallback;
  } catch {
    return fallback;
  }
};

const toDisplayError = (error, fallbackMessage) => {
  const detailRows = Array.isArray(error?.details) ? error.details : [];
  if (!detailRows.length) return error?.message || fallbackMessage;
  const firstDetail = detailRows[0];
  if (typeof firstDetail === 'string') return `${error.message || fallbackMessage}: ${firstDetail}`;
  const detailMessage = firstDetail?.msg || firstDetail?.message || JSON.stringify(firstDetail);
  return `${error.message || fallbackMessage}: ${detailMessage}`;
};

const objectToMappingRows = (objectValue = {}) => {
  const entries = Object.entries(objectValue || {});
  if (!entries.length) return [{ key: '', value: '' }];
  return entries.map(([key, value]) => ({ key, value: String(value ?? '') }));
};

const mappingRowsToObject = (rows) => rows.reduce((acc, row) => {
  const key = String(row.key || '').trim();
  const value = String(row.value || '').trim();
  if (!key) return acc;
  acc[key] = value;
  return acc;
}, {});

const rowsToCsv = (rows) => {
  if (!Array.isArray(rows) || !rows.length) return '';
  const headers = Array.from(new Set(rows.flatMap((row) => Object.keys(row || {}))));
  const escape = (value) => {
    const text = value == null ? '' : (typeof value === 'object' ? JSON.stringify(value) : String(value));
    if (text.includes('"') || text.includes(',') || text.includes('\n')) {
      return `"${text.replace(/"/g, '""')}"`;
    }
    return text;
  };
  const lines = [headers.join(',')];
  rows.forEach((row) => lines.push(headers.map((header) => escape(row[header])).join(',')));
  return lines.join('\n');
};

const downloadFile = (filename, content, contentType) => {
  const blob = new Blob([content], { type: contentType });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
};

const getValueByPath = (objectValue, pathValue) => {
  const path = String(pathValue || '').trim();
  if (!path) return undefined;
  return path.split('.').reduce((acc, key) => {
    if (acc == null) return undefined;
    return acc[key];
  }, objectValue);
};

const applyRecordTemplate = (template, record) => {
  const text = String(template || '');
  return text.replace(/\{\{\s*record\.([^}]+)\s*\}\}/g, (_full, tokenPath) => {
    const value = getValueByPath(record, tokenPath.trim());
    if (value == null) return '';
    if (typeof value === 'object') return JSON.stringify(value);
    return String(value);
  });
};

const MappingEditor = ({ title, rows, setRows }) => {
  const addRow = () => setRows((current) => [...current, { key: '', value: '' }]);
  const updateRow = (index, field, nextValue) => setRows((current) => current.map((row, rowIndex) => (rowIndex === index ? { ...row, [field]: nextValue } : row)));
  const removeRow = (index) => setRows((current) => (current.length > 1 ? current.filter((_, rowIndex) => rowIndex !== index) : current));

  return (
    <div className="md:col-span-2 rounded-lg border border-slate-700/60 bg-slate-900/35 p-3">
      <div className="flex items-center justify-between mb-2">
        <p className="text-xs text-slate-300">{title}</p>
        <button type="button" onClick={addRow} className="text-xs text-red-300 hover:text-red-200">+ Add mapping</button>
      </div>
      <div className="space-y-2">
        {rows.map((row, index) => (
          <div key={`${title}-${index}`} className="grid grid-cols-[1fr_1fr_auto] gap-2">
            <input placeholder="target_key" value={row.key} onChange={(event) => updateRow(index, 'key', event.target.value)} className="px-2 py-1.5 bg-slate-800 border border-slate-600 rounded text-xs text-slate-100" />
            <input placeholder="{{record.field}}" value={row.value} onChange={(event) => updateRow(index, 'value', event.target.value)} className="px-2 py-1.5 bg-slate-800 border border-slate-600 rounded text-xs text-slate-100" />
            <button type="button" onClick={() => removeRow(index)} className="px-2 py-1.5 text-xs text-slate-300 hover:text-slate-100">Remove</button>
          </div>
        ))}
      </div>
    </div>
  );
};

const Reporting = () => {
  const [searchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState('dashboard');
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState('');

  const [integrations, setIntegrations] = useState([]);
  const [automationStatus, setAutomationStatus] = useState(null);
  const [signupsRows, setSignupsRows] = useState([]);
  const [categoryRows, setCategoryRows] = useState([]);
  const [apiStatsRows, setApiStatsRows] = useState([]);
  const [pixelPerformanceRows, setPixelPerformanceRows] = useState([]);
  const [reportingOverview, setReportingOverview] = useState(null);
  const [connections, setConnections] = useState([]);
  const [customReports, setCustomReports] = useState([]);
  const [reportScheduleStatus, setReportScheduleStatus] = useState(null);
  const [reportRuns, setReportRuns] = useState([]);

  const [runStatusFilter, setRunStatusFilter] = useState('');
  const [runTypeFilter, setRunTypeFilter] = useState('');
  const [runRangeDays, setRunRangeDays] = useState('30');
  const [runSearch, setRunSearch] = useState('');

  const [integrationForm, setIntegrationForm] = useState(initialIntegrationForm);
  const [integrationQueryRows, setIntegrationQueryRows] = useState(objectToMappingRows({ since: '{{since_iso}}' }));
  const [pixelQueryRows, setPixelQueryRows] = useState(objectToMappingRows({}));
  const [normalizationRows, setNormalizationRows] = useState(objectToMappingRows({ conversion_id: '{{record.id}}' }));
  const [sampleResponseJson, setSampleResponseJson] = useState('{"data":[{"id":"123","status":"approved","amount":"12.50"}]}');

  const [connectionForm, setConnectionForm] = useState(initialConnectionForm);
  const [reportForm, setReportForm] = useState(initialReportForm);
  const [scheduleForm, setScheduleForm] = useState(initialScheduleForm);
  const [adHocResult, setAdHocResult] = useState(null);

  const fetchData = async ({ soft = false } = {}) => {
    try {
      setError('');
      if (soft) setRefreshing(true); else setLoading(true);

      const reportRunsParams = { limit: 50 };
      if (runStatusFilter) reportRunsParams.status = runStatusFilter;
      if (runTypeFilter) reportRunsParams.run_type = runTypeFilter;
      if (runSearch.trim()) reportRunsParams.search = runSearch.trim();
      const parsedDays = parseInt(runRangeDays, 10);
      if (!Number.isNaN(parsedDays) && parsedDays > 0) {
        const fromDate = new Date(Date.now() - parsedDays * 24 * 60 * 60 * 1000);
        reportRunsParams.from = fromDate.toISOString();
      }

      const [
        integrationsResponse,
        automationResponse,
        reportingOverviewResponse,
        signupsResponse,
        categoriesResponse,
        apiStatsResponse,
        pixelPerformanceResponse,
        connectionsResponse,
        customReportsResponse,
        reportScheduleResponse,
        reportRunsResponse
      ] = await Promise.all([
        adminReportingAPI.listAdvertiserApis(),
        adminReportingAPI.advertiserAutomationStatus(),
        adminReportingAPI.reportingOverview(),
        adminReportingAPI.signupsReport({ group_by: 'day' }),
        adminReportingAPI.categoriesReport(),
        adminReportingAPI.advertiserApiStatsReport(),
        adminReportingAPI.pixelPerformanceReport(),
        adminReportingAPI.listExternalConnections(),
        adminReportingAPI.listCustomReports(),
        adminReportingAPI.reportScheduleStatus(),
        adminReportingAPI.reportRuns(reportRunsParams)
      ]);

      setIntegrations(integrationsResponse?.data?.integrations || []);
      setAutomationStatus(automationResponse?.data || null);
      setReportingOverview(reportingOverviewResponse?.data?.overview || null);
      setSignupsRows(signupsResponse?.data?.rows || []);
      setCategoryRows(categoriesResponse?.data?.rows || []);
      setApiStatsRows(apiStatsResponse?.data?.rows || []);
      setPixelPerformanceRows(pixelPerformanceResponse?.data?.rows || []);
      setConnections(connectionsResponse?.data?.connections || []);
      setCustomReports(customReportsResponse?.data?.reports || []);
      setReportScheduleStatus(reportScheduleResponse?.data || null);
      setReportRuns(reportRunsResponse?.data?.runs || []);
    } catch (requestError) {
      setError(toDisplayError(requestError, 'Failed to load reporting workspace'));
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => { fetchData(); }, []);

  useEffect(() => {
    if (!loading) fetchData({ soft: true });
  }, [runStatusFilter, runTypeFilter, runRangeDays, runSearch]);

  useEffect(() => {
    const requestedTab = searchParams.get('tab');
    if (requestedTab && tabs.some((tab) => tab.id === requestedTab)) setActiveTab(requestedTab);
  }, [searchParams]);

  const handleCreateIntegration = async (event) => {
    event.preventDefault();
    try {
      setError('');
      await adminReportingAPI.createAdvertiserApi({
        ...integrationForm,
        poll_interval_minutes: Number(integrationForm.poll_interval_minutes || 60),
        lookback_minutes: Number(integrationForm.lookback_minutes || 30),
        request_query: mappingRowsToObject(integrationQueryRows),
        pixel_query: mappingRowsToObject(pixelQueryRows),
        normalization_map: mappingRowsToObject(normalizationRows)
      });
      setIntegrationForm(initialIntegrationForm);
      setIntegrationQueryRows(objectToMappingRows({ since: '{{since_iso}}' }));
      setPixelQueryRows(objectToMappingRows({}));
      setNormalizationRows(objectToMappingRows({ conversion_id: '{{record.id}}' }));
      await fetchData({ soft: true });
    } catch (requestError) {
      setError(toDisplayError(requestError, 'Could not save advertiser API integration'));
    }
  };

  const handleCreateConnection = async (event) => {
    event.preventDefault();
    try {
      setError('');
      await adminReportingAPI.createExternalConnection({
        ...connectionForm,
        auth_config: parseJsonField(connectionForm.auth_config, {}),
        default_headers: parseJsonField(connectionForm.default_headers, {})
      });
      setConnectionForm(initialConnectionForm);
      await fetchData({ soft: true });
    } catch (requestError) {
      setError(toDisplayError(requestError, 'Could not save external connection'));
    }
  };

  const handleCreateCustomReport = async (event) => {
    event.preventDefault();
    try {
      setError('');
      await adminReportingAPI.createCustomReport({ ...reportForm, config: parseJsonField(reportForm.config, {}) });
      setReportForm(initialReportForm);
      await fetchData({ soft: true });
    } catch (requestError) {
      setError(toDisplayError(requestError, 'Could not create custom report'));
    }
  };

  const handleCreateSchedule = async (event) => {
    event.preventDefault();
    try {
      setError('');
      await adminReportingAPI.createReportSchedule({
        ...scheduleForm,
        report_id: Number(scheduleForm.report_id),
        interval_minutes: Number(scheduleForm.interval_minutes || 60)
      });
      setScheduleForm(initialScheduleForm);
      await fetchData({ soft: true });
    } catch (requestError) {
      setError(toDisplayError(requestError, 'Could not create schedule'));
    }
  };

  const runAdHocReport = async () => {
    try {
      setError('');
      const response = await adminReportingAPI.runAdHocReport({ dataset: reportForm.dataset, config: parseJsonField(reportForm.config, {}) });
      setAdHocResult(response?.data || null);
      await fetchData({ soft: true });
    } catch (requestError) {
      setError(toDisplayError(requestError, 'Could not run ad-hoc report'));
    }
  };

  const handleRunSavedReport = async (reportId) => {
    try {
      setError('');
      const response = await adminReportingAPI.runCustomReport(reportId);
      setAdHocResult(response?.data || null);
      await fetchData({ soft: true });
    } catch (requestError) {
      setError(toDisplayError(requestError, 'Could not run report'));
    }
  };

  const handleExportReport = async (report, format = 'csv') => {
    try {
      const response = await adminReportingAPI.runCustomReport(report.id);
      const rows = response?.data?.rows;
      const listRows = Array.isArray(rows) ? rows : [rows];
      if (format === 'csv') {
        downloadFile(`${report.name.replace(/\s+/g, '-').toLowerCase()}.csv`, rowsToCsv(listRows), 'text/csv;charset=utf-8');
      } else {
        downloadFile(`${report.name.replace(/\s+/g, '-').toLowerCase()}.json`, JSON.stringify(listRows, null, 2), 'application/json;charset=utf-8');
      }
      await fetchData({ soft: true });
    } catch (requestError) {
      setError(toDisplayError(requestError, 'Could not export report'));
    }
  };

  const scheduleRows = useMemo(() => reportScheduleStatus?.schedules || [], [reportScheduleStatus]);
  const scheduleSuccessRows = useMemo(() => scheduleRows.filter((item) => item.last_status === 'success').slice(0, 10), [scheduleRows]);
  const mappingPreviewRows = useMemo(() => {
    try {
      const parsed = JSON.parse(sampleResponseJson || '{}');
      const source = integrationForm.response_path ? getValueByPath(parsed, integrationForm.response_path) : parsed;
      const records = Array.isArray(source) ? source : (source ? [source] : []);
      const mapObject = mappingRowsToObject(normalizationRows);
      return records.slice(0, 5).map((record) => Object.entries(mapObject).reduce((acc, [targetKey, template]) => {
        acc[targetKey] = applyRecordTemplate(template, record);
        return acc;
      }, {}));
    } catch {
      return [];
    }
  }, [sampleResponseJson, integrationForm.response_path, normalizationRows]);

  if (loading) {
    return <div className="h-80 flex items-center justify-center text-slate-400">Loading reporting workspace...</div>;
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-3">
        <div>
          <h2 className="text-3xl font-bold text-slate-100">Reporting</h2>
          <p className="text-slate-400 mt-2">Dashboard for completed reports, builder workflows, and advertiser reporting API operations.</p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <button onClick={() => fetchData({ soft: true })} className="px-4 py-2 bg-slate-800 hover:bg-slate-700 text-slate-100 rounded-lg inline-flex items-center"><RefreshCcw className="w-4 h-4 mr-2" />{refreshing ? 'Refreshing...' : 'Refresh'}</button>
          <button onClick={() => setActiveTab('builder')} className="px-4 py-2 bg-red-700 hover:bg-red-600 text-white rounded-lg inline-flex items-center"><FilePlus className="w-4 h-4 mr-2" />Open Report Builder</button>
        </div>
      </div>

      <div className="admin-glass rounded-xl border border-slate-700/50 p-2 flex flex-wrap gap-2">
        {tabs.map((tab) => {
          const TabIcon = tab.icon;
          return (
            <button key={tab.id} onClick={() => setActiveTab(tab.id)} className={`px-3 py-2 text-sm rounded-lg border inline-flex items-center transition-colors ${activeTab === tab.id ? 'bg-red-700/20 border-red-600/50 text-red-200' : 'bg-slate-900/40 border-slate-700 text-slate-300 hover:text-slate-100'}`}>
              <TabIcon className="w-4 h-4 mr-2" />{tab.label}
            </button>
          );
        })}
      </div>

      {error && <div className="rounded-lg border border-red-700/40 bg-red-900/20 p-3 text-red-200 text-sm">{error}</div>}

      {activeTab === 'dashboard' && (
        <>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="admin-glass rounded-xl p-4 border border-slate-700/50"><p className="text-slate-400 text-sm">Active Integrations</p><p className="text-3xl text-slate-100 font-bold mt-2">{reportingOverview?.active_integrations ?? integrations.filter((item) => item.is_active).length}</p></div>
            <div className="admin-glass rounded-xl p-4 border border-slate-700/50"><p className="text-slate-400 text-sm">Due Pulls</p><p className="text-3xl text-slate-100 font-bold mt-2">{automationStatus?.due_integrations ?? 0}</p></div>
            <div className="admin-glass rounded-xl p-4 border border-slate-700/50"><p className="text-slate-400 text-sm">Report Runs (24h)</p><p className="text-3xl text-slate-100 font-bold mt-2">{reportingOverview?.report_runs_last_24h ?? 0}</p></div>
            <div className="admin-glass rounded-xl p-4 border border-slate-700/50"><p className="text-slate-400 text-sm">Pixel Delivery Rate</p><p className="text-3xl text-slate-100 font-bold mt-2">{reportingOverview?.pixel_delivery_rate ?? 0}%</p></div>
          </div>

          <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
            <div className="admin-glass rounded-xl border border-slate-700/50 overflow-hidden">
              <div className="p-4 border-b border-slate-700/50 flex items-center justify-between"><h3 className="text-slate-100 font-semibold inline-flex items-center"><CheckCircle2 className="w-4 h-4 mr-2 text-emerald-300" />Finished Reports</h3><p className="text-xs text-slate-500">{reportRuns.length} recent</p></div>
              <div className="px-4 py-3 border-b border-slate-700/50 grid grid-cols-1 md:grid-cols-4 gap-2">
                <select value={runStatusFilter} onChange={(event) => setRunStatusFilter(event.target.value)} className="px-2 py-1.5 bg-slate-800 border border-slate-600 rounded text-xs text-slate-100"><option value="">All statuses</option><option value="completed">Completed</option><option value="failed">Failed</option><option value="running">Running</option></select>
                <select value={runTypeFilter} onChange={(event) => setRunTypeFilter(event.target.value)} className="px-2 py-1.5 bg-slate-800 border border-slate-600 rounded text-xs text-slate-100"><option value="">All run types</option><option value="saved">Saved report</option><option value="ad_hoc">Ad-hoc</option><option value="export">Export</option><option value="scheduled">Scheduled</option></select>
                <select value={runRangeDays} onChange={(event) => setRunRangeDays(event.target.value)} className="px-2 py-1.5 bg-slate-800 border border-slate-600 rounded text-xs text-slate-100"><option value="7">Last 7 days</option><option value="30">Last 30 days</option><option value="90">Last 90 days</option><option value="365">Last 365 days</option></select>
                <input value={runSearch} onChange={(event) => setRunSearch(event.target.value)} placeholder="Search report name" className="px-2 py-1.5 bg-slate-800 border border-slate-600 rounded text-xs text-slate-100" />
              </div>
              <div className="p-4 space-y-2 max-h-72 overflow-auto">
                {reportRuns.length === 0 ? <p className="text-sm text-slate-400">No finished reports yet.</p> : reportRuns.map((item) => <div key={item.id} className="rounded-md border border-slate-700 bg-slate-900/40 p-2 text-xs"><p className="text-slate-200">{item.report_name}</p><p className="text-slate-500">{item.run_type} · {item.status} · {item.row_count} rows · {new Date(item.completed_at || item.started_at).toLocaleString()}</p></div>)}
              </div>
            </div>

            <div className="admin-glass rounded-xl border border-slate-700/50 overflow-hidden">
              <div className="p-4 border-b border-slate-700/50 flex items-center justify-between"><h3 className="text-slate-100 font-semibold inline-flex items-center"><ListChecks className="w-4 h-4 mr-2 text-red-300" />Successful Scheduled Deliveries</h3><p className="text-xs text-slate-500">{scheduleSuccessRows.length} recent</p></div>
              <div className="p-4 space-y-2 max-h-72 overflow-auto">
                {scheduleSuccessRows.length === 0 ? <p className="text-sm text-slate-400">No successful scheduled deliveries yet.</p> : scheduleSuccessRows.map((schedule) => <div key={schedule.id} className="rounded-md border border-slate-700 bg-slate-900/40 p-2 text-xs"><p className="text-slate-200">{schedule.name} ({schedule.report_name || `Report #${schedule.report_id}`})</p><p className="text-slate-500">Last success: {schedule.last_run_at ? new Date(schedule.last_run_at).toLocaleString() : 'N/A'}</p></div>)}
              </div>
            </div>
          </div>
        </>
      )}

      {activeTab === 'builder' && (
        <>
          <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
            <div className="admin-glass rounded-xl border border-slate-700/50 overflow-hidden">
              <div className="p-4 border-b border-slate-700/50"><h3 className="text-slate-100 font-semibold inline-flex items-center"><BarChart3 className="w-4 h-4 mr-2 text-red-300" />Custom Report Builder</h3></div>
              <form onSubmit={handleCreateCustomReport} className="p-4 grid grid-cols-1 gap-3 border-b border-slate-700/50">
                <input required placeholder="Report name" value={reportForm.name} onChange={(event) => setReportForm((state) => ({ ...state, name: event.target.value }))} className="px-3 py-2 bg-slate-800 border border-slate-600 rounded-lg text-slate-100" />
                <select value={reportForm.dataset} onChange={(event) => setReportForm((state) => ({ ...state, dataset: event.target.value }))} className="px-3 py-2 bg-slate-800 border border-slate-600 rounded-lg text-slate-100"><option value="signups">signups</option><option value="categories">categories</option><option value="advertiser_api_stats">advertiser_api_stats</option><option value="advertiser_api_records">advertiser_api_records</option><option value="external_connection">external_connection</option></select>
                <textarea placeholder="Config JSON" value={reportForm.config} onChange={(event) => setReportForm((state) => ({ ...state, config: event.target.value }))} className="px-3 py-2 min-h-20 bg-slate-800 border border-slate-600 rounded-lg text-slate-100 font-mono text-xs" />
                <div className="flex gap-2"><button type="submit" className="flex-1 px-4 py-2 bg-red-700 hover:bg-red-600 text-white rounded-lg">Save report</button><button type="button" onClick={runAdHocReport} className="flex-1 px-4 py-2 bg-slate-800 hover:bg-slate-700 text-slate-100 rounded-lg">Run ad-hoc</button></div>
              </form>
              <div className="p-4 space-y-2 max-h-60 overflow-auto">{customReports.map((report) => <div key={report.id} className="text-xs rounded-md border border-slate-700 bg-slate-900/40 p-2 flex justify-between items-center gap-2"><div><p className="text-slate-200">{report.name}</p><p className="text-slate-500">{report.dataset}</p></div><div className="flex gap-1"><button onClick={() => handleRunSavedReport(report.id)} className="px-2 py-1 rounded bg-slate-800 text-slate-100">Run</button><button onClick={() => handleExportReport(report, 'csv')} className="px-2 py-1 rounded bg-slate-700 text-slate-100">CSV</button><button onClick={() => handleExportReport(report, 'json')} className="px-2 py-1 rounded bg-slate-700 text-slate-100">JSON</button></div></div>)}</div>
            </div>

            <div className="admin-glass rounded-xl border border-slate-700/50 overflow-hidden">
              <div className="p-4 border-b border-slate-700/50"><h3 className="text-slate-100 font-semibold inline-flex items-center"><Link2 className="w-4 h-4 mr-2 text-red-300" />External Data Connections (encrypted auth)</h3></div>
              <form onSubmit={handleCreateConnection} className="p-4 grid grid-cols-1 md:grid-cols-2 gap-3 border-b border-slate-700/50">
                <input required placeholder="Connection name" value={connectionForm.name} onChange={(event) => setConnectionForm((state) => ({ ...state, name: event.target.value }))} className="px-3 py-2 bg-slate-800 border border-slate-600 rounded-lg text-slate-100" />
                <input required placeholder="https://api.partner.com" value={connectionForm.base_url} onChange={(event) => setConnectionForm((state) => ({ ...state, base_url: event.target.value }))} className="px-3 py-2 bg-slate-800 border border-slate-600 rounded-lg text-slate-100" />
                <select value={connectionForm.connection_type} onChange={(event) => setConnectionForm((state) => ({ ...state, connection_type: event.target.value }))} className="px-3 py-2 bg-slate-800 border border-slate-600 rounded-lg text-slate-100"><option value="http">http</option><option value="webhook">webhook</option><option value="database">database</option></select>
                <select value={connectionForm.auth_type} onChange={(event) => setConnectionForm((state) => ({ ...state, auth_type: event.target.value }))} className="px-3 py-2 bg-slate-800 border border-slate-600 rounded-lg text-slate-100"><option value="none">none</option><option value="bearer">bearer</option><option value="basic">basic</option><option value="header">header</option></select>
                <textarea placeholder="Auth config JSON" value={connectionForm.auth_config} onChange={(event) => setConnectionForm((state) => ({ ...state, auth_config: event.target.value }))} className="md:col-span-2 px-3 py-2 min-h-20 bg-slate-800 border border-slate-600 rounded-lg text-slate-100 font-mono text-xs" />
                <button type="submit" className="md:col-span-2 px-4 py-2 bg-red-700 hover:bg-red-600 text-white rounded-lg">Save external connection</button>
              </form>
              <div className="p-4 space-y-2 max-h-52 overflow-auto">{connections.map((connection) => <div key={connection.id} className="text-xs rounded-md border border-slate-700 bg-slate-900/40 p-2"><p className="text-slate-200">{connection.name}</p><p className="text-slate-500 break-all">{connection.base_url}</p></div>)}</div>
            </div>
          </div>

          <div className="admin-glass rounded-xl border border-slate-700/50 overflow-hidden">
            <div className="p-4 border-b border-slate-700/50 flex items-center justify-between"><h3 className="text-slate-100 font-semibold">Scheduled Report Delivery</h3><p className="text-xs text-slate-500">Webhook delivery for saved reports</p></div>
            <form onSubmit={handleCreateSchedule} className="p-4 grid grid-cols-1 md:grid-cols-5 gap-3 border-b border-slate-700/50">
              <select required value={scheduleForm.report_id} onChange={(event) => setScheduleForm((state) => ({ ...state, report_id: event.target.value }))} className="px-3 py-2 bg-slate-800 border border-slate-600 rounded-lg text-slate-100"><option value="">Select report</option>{customReports.map((report) => <option key={report.id} value={report.id}>{report.name}</option>)}</select>
              <input required placeholder="Schedule name" value={scheduleForm.name} onChange={(event) => setScheduleForm((state) => ({ ...state, name: event.target.value }))} className="px-3 py-2 bg-slate-800 border border-slate-600 rounded-lg text-slate-100" />
              <input required placeholder="https://your-webhook" value={scheduleForm.destination_url} onChange={(event) => setScheduleForm((state) => ({ ...state, destination_url: event.target.value }))} className="px-3 py-2 bg-slate-800 border border-slate-600 rounded-lg text-slate-100" />
              <select value={scheduleForm.payload_format} onChange={(event) => setScheduleForm((state) => ({ ...state, payload_format: event.target.value }))} className="px-3 py-2 bg-slate-800 border border-slate-600 rounded-lg text-slate-100"><option value="json">json</option><option value="csv">csv</option></select>
              <div className="flex gap-2"><input type="number" min="1" value={scheduleForm.interval_minutes} onChange={(event) => setScheduleForm((state) => ({ ...state, interval_minutes: event.target.value }))} className="flex-1 px-3 py-2 bg-slate-800 border border-slate-600 rounded-lg text-slate-100" /><button type="submit" className="px-4 py-2 bg-red-700 hover:bg-red-600 text-white rounded-lg">Add</button></div>
            </form>
            <div className="p-4 space-y-2 max-h-56 overflow-auto">{scheduleRows.length === 0 ? <p className="text-sm text-slate-400">No schedules yet.</p> : scheduleRows.map((schedule) => <div key={schedule.id} className="rounded-md border border-slate-700 bg-slate-900/40 p-2 text-xs"><p className="text-slate-200">{schedule.name} ({schedule.report_name || `Report #${schedule.report_id}`})</p><p className="text-slate-500">Every {schedule.interval_minutes}m · {schedule.payload_format} · {schedule.destination_url}</p><p className="text-slate-500">Last: {schedule.last_status || 'never'} {schedule.last_error ? `- ${schedule.last_error}` : ''}</p></div>)}</div>
          </div>
        </>
      )}

      {activeTab === 'apis' && (
        <>
          <div className="flex gap-2 flex-wrap">
            <button onClick={async () => { await adminReportingAPI.runAdvertiserAutomation(); await fetchData({ soft: true }); }} className="px-4 py-2 bg-red-700 hover:bg-red-600 text-white rounded-lg inline-flex items-center"><PlayCircle className="w-4 h-4 mr-2" />Run Pull Automation</button>
            <a href="/automations" className="px-4 py-2 bg-slate-800 hover:bg-slate-700 text-slate-100 rounded-lg inline-flex items-center">Open Automations</a>
          </div>

          <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
            <div className="admin-glass rounded-xl border border-slate-700/50 overflow-hidden">
              <div className="p-4 border-b border-slate-700/50 flex items-center justify-between"><h3 className="text-slate-100 font-semibold inline-flex items-center"><Plug className="w-4 h-4 mr-2 text-red-300" />Advertiser Reporting API Setup</h3><p className="text-xs text-slate-500">Pull + Pixel + Mapping</p></div>
              <form onSubmit={handleCreateIntegration} className="p-4 grid grid-cols-1 md:grid-cols-2 gap-3">
                <input required placeholder="Integration name" value={integrationForm.name} onChange={(event) => setIntegrationForm((state) => ({ ...state, name: event.target.value }))} className="px-3 py-2 bg-slate-800 border border-slate-600 rounded-lg text-slate-100" />
                <input required placeholder="https://advertiser-api.com/events" value={integrationForm.endpoint_url} onChange={(event) => setIntegrationForm((state) => ({ ...state, endpoint_url: event.target.value }))} className="px-3 py-2 bg-slate-800 border border-slate-600 rounded-lg text-slate-100" />
                <select value={integrationForm.request_method} onChange={(event) => setIntegrationForm((state) => ({ ...state, request_method: event.target.value }))} className="px-3 py-2 bg-slate-800 border border-slate-600 rounded-lg text-slate-100"><option value="GET">GET</option><option value="POST">POST</option><option value="PUT">PUT</option><option value="PATCH">PATCH</option></select>
                <input placeholder="Response path" value={integrationForm.response_path} onChange={(event) => setIntegrationForm((state) => ({ ...state, response_path: event.target.value }))} className="px-3 py-2 bg-slate-800 border border-slate-600 rounded-lg text-slate-100" />
                <input placeholder="Record id field" value={integrationForm.record_id_field} onChange={(event) => setIntegrationForm((state) => ({ ...state, record_id_field: event.target.value }))} className="px-3 py-2 bg-slate-800 border border-slate-600 rounded-lg text-slate-100" />
                <input placeholder="Increment field" value={integrationForm.increment_field} onChange={(event) => setIntegrationForm((state) => ({ ...state, increment_field: event.target.value }))} className="px-3 py-2 bg-slate-800 border border-slate-600 rounded-lg text-slate-100" />
                <input type="number" min="1" placeholder="Poll minutes" value={integrationForm.poll_interval_minutes} onChange={(event) => setIntegrationForm((state) => ({ ...state, poll_interval_minutes: event.target.value }))} className="px-3 py-2 bg-slate-800 border border-slate-600 rounded-lg text-slate-100" />
                <input type="number" min="0" placeholder="Lookback minutes" value={integrationForm.lookback_minutes} onChange={(event) => setIntegrationForm((state) => ({ ...state, lookback_minutes: event.target.value }))} className="px-3 py-2 bg-slate-800 border border-slate-600 rounded-lg text-slate-100" />
                <input placeholder="Pixel URL template" value={integrationForm.pixel_url_template} onChange={(event) => setIntegrationForm((state) => ({ ...state, pixel_url_template: event.target.value }))} className="md:col-span-2 px-3 py-2 bg-slate-800 border border-slate-600 rounded-lg text-slate-100" />
                <MappingEditor title="Request Query Mapping" rows={integrationQueryRows} setRows={setIntegrationQueryRows} />
                <MappingEditor title="Pixel Query Mapping" rows={pixelQueryRows} setRows={setPixelQueryRows} />
                <MappingEditor title="Normalization Mapping" rows={normalizationRows} setRows={setNormalizationRows} />
                <div className="md:col-span-2 rounded-lg border border-slate-700/60 bg-slate-900/35 p-3">
                  <p className="text-xs text-slate-300 mb-2">Response Mapping Preview</p>
                  <textarea value={sampleResponseJson} onChange={(event) => setSampleResponseJson(event.target.value)} className="w-full min-h-28 px-3 py-2 bg-slate-800 border border-slate-600 rounded-lg text-slate-100 font-mono text-xs" />
                  <div className="mt-2 max-h-40 overflow-auto rounded-md border border-slate-700 bg-slate-950/40 p-2">
                    {mappingPreviewRows.length === 0 ? <p className="text-xs text-slate-500">No mapped preview rows. Check sample JSON and response path.</p> : mappingPreviewRows.map((row, index) => <pre key={`mapping-preview-${index}`} className="text-xs text-slate-200 mb-2">{JSON.stringify(row, null, 2)}</pre>)}
                  </div>
                </div>
                <button type="submit" className="md:col-span-2 px-4 py-2 bg-red-700 hover:bg-red-600 text-white rounded-lg inline-flex items-center justify-center"><FilePlus className="w-4 h-4 mr-2" />Save Integration</button>
              </form>
            </div>

            <div className="admin-glass rounded-xl border border-slate-700/50 overflow-hidden">
              <div className="p-4 border-b border-slate-700/50"><h3 className="text-slate-100 font-semibold inline-flex items-center"><ListChecks className="w-4 h-4 mr-2 text-red-300" />Configured Integrations</h3></div>
              <div className="p-4 space-y-3 max-h-[760px] overflow-auto">
                {integrations.length === 0 ? <p className="text-sm text-slate-400">No advertiser APIs configured yet.</p> : integrations.map((integration) => <div key={integration.id} className="rounded-lg border border-slate-700 bg-slate-900/40 p-3"><div className="flex items-center justify-between gap-3"><div><p className="text-slate-100 font-medium">{integration.name}</p><p className="text-xs text-slate-500 break-all">{integration.endpoint_url}</p></div><button onClick={async () => { await adminReportingAPI.runAdvertiserApi(integration.id); await fetchData({ soft: true }); }} className="px-3 py-1.5 text-xs rounded-md bg-red-700 hover:bg-red-600 text-white">Run now</button></div><div className="mt-2 text-xs text-slate-400 flex flex-wrap gap-3"><span>Status: {integration.is_active ? 'active' : 'paused'}</span><span>Runs: {integration.total_runs}</span><span>Stored: {integration.total_records_stored}</span><span>Conversions: {integration.total_conversions_fired}</span></div></div>)}
              </div>
            </div>
          </div>
        </>
      )}

      {adHocResult && (
        <div className="admin-glass rounded-xl border border-slate-700/50 overflow-hidden">
          <div className="p-4 border-b border-slate-700/50"><h3 className="text-slate-100 font-semibold">Report Output Preview</h3></div>
          <pre className="p-4 text-xs text-slate-200 overflow-auto max-h-72 bg-slate-950/40">{JSON.stringify(adHocResult.rows, null, 2)}</pre>
        </div>
      )}
    </div>
  );
};

export default Reporting;
